import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  HomeIcon, 
  UserGroupIcon, 
  DocumentTextIcon, 
  BriefcaseIcon,
  BookOpenIcon,
  CurrencyDollarIcon,
  Cog6ToothIcon,
  ChartBarIcon,
  AcademicCapIcon,
  QuestionMarkCircleIcon,
  UserPlusIcon,
  BuildingOffice2Icon,
  BanknotesIcon,
  BuildingStorefrontIcon,
  ArchiveBoxIcon,
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
  ScaleIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import Tooltip from '../ui/Tooltip';
import faviconLogo from '../../favicon.png';

// `roles` (when present) restricts visibility. Roles allowed to see financial
// sections are the only ones with backend Billing access — see
// accounts/permissions.py::BillingPermission.
const FINANCIAL_ROLES = ['admin', 'owner', 'attorney', 'accountant'];

const navigation = [
  { name: 'Dashboard', href: '/', icon: HomeIcon },
  { name: 'Clients', href: '/clients', icon: UserGroupIcon },
  { name: 'Cases', href: '/cases', icon: BriefcaseIcon },
  { name: 'Documents', href: '/documents', icon: DocumentTextIcon },
  { name: 'Diary', href: '/diary', icon: BookOpenIcon },
  { name: 'Billing', href: '/billing', icon: CurrencyDollarIcon, roles: FINANCIAL_ROLES },
  { name: 'Trust Accounts', href: '/trust', icon: BanknotesIcon, roles: FINANCIAL_ROLES },
  { name: 'Estates', href: '/estates', icon: ArchiveBoxIcon },
  { name: 'Conveyancing', href: '/conveyancing', icon: BuildingStorefrontIcon },
  { name: 'Research', href: '/research', icon: AcademicCapIcon },
  { name: 'Analytics', href: '/analytics', icon: ChartBarIcon, id: 'main-analytics' },
];

// Legal Library — platform-wide; appears for EVERY authenticated role
// (firm users and students alike). Highlighted on any /library route.
const libraryNavItem = { name: 'Library', href: '/library', icon: BookOpenIcon, match: '/library' };

// Law-student (Academy) navigation — replaces the firm nav entirely for students.
const studentNavigation = [
  { name: 'Dashboard', href: '/', icon: HomeIcon },
  { name: 'Study', href: '/student/study', icon: BookOpenIcon },
  { name: 'Mock Trials', href: '/student/mock-trials', icon: ScaleIcon },
  libraryNavItem,
  { name: 'Progress', href: '/student/progress', icon: ChartBarIcon },
];

const adminNavigation = [
  { name: 'Admin Dashboard', href: '/admin', icon: Cog6ToothIcon },
  { name: 'User Management', href: '/admin/users', icon: UserGroupIcon },
  { name: 'System Settings', href: '/admin/settings', icon: Cog6ToothIcon },
  { name: 'LLM Integration', href: '/admin/llm-integration', icon: AcademicCapIcon },
  { name: 'Analytics', href: '/admin/analytics', icon: ChartBarIcon, id: 'admin-analytics' },
];

const orgManagementNav = [
  { name: 'Team Management', href: '/team', icon: UserPlusIcon },
  { name: 'Organization Settings', href: '#org-settings', icon: BuildingOffice2Icon, action: 'openOrgModal' },
];

const NavItemContent = ({ item, isActive, collapsed, isMobile }) => {
  const iconEl = (
    <item.icon
      className={`
        h-5 w-5 flex-shrink-0 transition-colors duration-150
        ${isActive
          ? 'text-primary-500 dark:text-primary-400'
          : 'text-gray-500 dark:text-gray-400 group-hover:text-gray-700 dark:group-hover:text-gray-300'}
      `}
      aria-hidden="true"
    />
  );

  if (collapsed && !isMobile) {
    return (
      <Tooltip content={item.name} position="right" delay={100}>
        <div className="flex items-center justify-center w-full py-2">
          {iconEl}
        </div>
      </Tooltip>
    );
  }

  return (
    <>
      <span className="mr-3">{iconEl}</span>
      <span className="truncate">{item.name}</span>
    </>
  );
};

const Sidebar = ({ isMobile, setSidebarOpen, showAdminLink = false, collapsed = false, onToggleCollapse }) => {
  const { user, showOnboarding } = useAuth();
  const location = useLocation();
  const [helpOpen, setHelpOpen] = useState(false);

  const isAdminUser = user?.role === 'admin' || user?.role === 'owner' || user?.is_admin === true;
  // Team & org-settings links navigate to admin-only pages; only show to admins/owners.
  const showOrgSection = isAdminUser;
  const userRole = user?.role;

  const allowedByRole = (item) => !item.roles || (userRole && item.roles.includes(userRole));
  const isStudent = userRole === 'student';

  // Items with a `match` prefix stay highlighted on nested routes
  // (e.g. Library on /library/:id); others require an exact path match.
  const isItemActive = (item) =>
    item.match ? location.pathname.startsWith(item.match) : location.pathname === item.href;

  // Students get a dedicated study nav and never see the firm/admin sections.
  let navItems;
  if (isStudent) {
    navItems = [...studentNavigation];
  } else {
    // Library is shared across all roles — append it to the firm nav too.
    navItems = [...navigation.filter(allowedByRole), libraryNavItem];
    if (showOrgSection) {
      navItems = [...navItems, { divider: true, label: 'Organization' }, ...orgManagementNav.filter(allowedByRole)];
    }
    if (isAdminUser) {
      navItems = [...navItems, { divider: true, label: 'Admin' }, ...adminNavigation.filter(allowedByRole)];
    }
  }

  const handleNavClick = () => {
    if (isMobile) {
      setSidebarOpen(false);
    }
  };

  const isCollapsed = collapsed && !isMobile;

  return (
    <div className="flex flex-col h-full overflow-y-auto bg-white dark:bg-gray-800 shadow-md" data-testid="sidebar">
      {/* Logo + Collapse toggle */}
      <div className={`flex items-center flex-shrink-0 border-b border-gray-200 dark:border-gray-700 h-16 ${
        isCollapsed ? 'justify-center px-2' : 'px-4'
      } py-5`}>
        <img src={faviconLogo} alt="EasyLaw" className="h-8 w-8 flex-shrink-0" />
        {!isCollapsed && (
          <span className="ml-2 text-xl font-semibold text-gray-900 dark:text-white truncate flex-1">
            EasyLaw
          </span>
        )}
        {!isMobile && onToggleCollapse && (
          <Tooltip content={collapsed ? 'Expand sidebar ([)' : 'Collapse sidebar ([)'} position="right" delay={200}>
            <button
              onClick={onToggleCollapse}
              data-testid="sidebar-collapse-toggle"
              className={`
                flex items-center justify-center rounded-md p-1.5 transition-colors duration-150
                text-gray-400 dark:text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700
                hover:text-gray-600 dark:hover:text-gray-300
                ${isCollapsed ? 'mt-0' : 'ml-2'}
              `}
              aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              {collapsed ? (
                <ChevronDoubleRightIcon className="h-4 w-4" aria-hidden="true" />
              ) : (
                <ChevronDoubleLeftIcon className="h-4 w-4" aria-hidden="true" />
              )}
            </button>
          </Tooltip>
        )}
      </div>

      {/* Navigation */}
      <div className={`flex-1 flex flex-col mt-2 ${isCollapsed ? 'px-1.5' : 'px-3'}`}>
        <nav className="flex-1 space-y-1">
          {navItems.map((item, idx) => 
            item.divider ? (
              <div key={`divider-${idx}`} className={`border-t border-gray-200 dark:border-gray-700 my-4 ${
                isCollapsed ? 'mx-1' : ''
              }`}>
                {item.label && !isCollapsed && (
                  <p className="px-3 pt-3 text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider">
                    {item.label}
                  </p>
                )}
              </div>
            ) : item.action === 'openOrgModal' ? (
              <button
                key={item.id || `${item.name}-${item.href}`}
                onClick={() => {
                  handleNavClick();
                  showOnboarding();
                }}
                className={`
                  w-full group flex items-center text-sm font-medium rounded-md transition-colors duration-150
                  text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700
                  ${isCollapsed ? 'justify-center px-2 py-2' : 'px-3 py-2'}
                `}
                title={isCollapsed ? item.name : undefined}
              >
                <NavItemContent item={item} isActive={false} collapsed={collapsed} isMobile={isMobile} />
              </button>
            ) : (
              <Link
                key={item.id || `${item.name}-${item.href}`}
                to={item.href}
                onClick={handleNavClick}
                className={`
                  group flex items-center text-sm font-medium rounded-md transition-colors duration-150
                  ${isCollapsed ? 'justify-center px-2 py-2' : 'px-3 py-2'}
                  ${isItemActive(item)
                    ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400'
                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'}
                `}
              >
                <NavItemContent item={item} isActive={isItemActive(item)} collapsed={collapsed} isMobile={isMobile} />
              </Link>
            )
          )}

          {/* Admin Link - Only visible to admin users */}
          {showAdminLink && (
            <div className="pt-4 mt-4 border-t border-gray-200 dark:border-gray-700">
              <Link
                to="/admin"
                className={`${
                  location.pathname.startsWith('/admin')
                    ? 'bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400'
                    : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                } group flex items-center text-sm font-medium rounded-md ${
                  isCollapsed ? 'justify-center px-2 py-2' : 'px-2 py-2'
                }`}
              >
                {isCollapsed ? (
                  <Tooltip content="Admin Dashboard" position="right" delay={100}>
                    <Cog6ToothIcon
                      className={`${
                        location.pathname.startsWith('/admin')
                          ? 'text-amber-500 dark:text-amber-400'
                          : 'text-gray-400 dark:text-gray-400 group-hover:text-gray-500 dark:group-hover:text-gray-300'
                      } flex-shrink-0 h-6 w-6`}
                    />
                  </Tooltip>
                ) : (
                  <>
                    <Cog6ToothIcon
                      className={`${
                        location.pathname.startsWith('/admin')
                          ? 'text-amber-500 dark:text-amber-400'
                          : 'text-gray-400 dark:text-gray-400 group-hover:text-gray-500 dark:group-hover:text-gray-300'
                      } mr-3 flex-shrink-0 h-6 w-6`}
                    />
                    Admin Dashboard
                  </>
                )}
              </Link>
            </div>
          )}
        </nav>
      </div>

      {/* Help section */}
      <div className={`mt-4 mb-2 ${isCollapsed ? 'px-1.5' : 'px-3'}`}>
        {isCollapsed ? (
          <Tooltip content="Help & Resources" position="right" delay={100}>
            <div className="cursor-pointer group flex items-center justify-center px-2 py-2 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700">
              <QuestionMarkCircleIcon className="h-5 w-5 text-gray-500 dark:text-gray-400" aria-hidden="true" />
            </div>
          </Tooltip>
        ) : (
          <>
            <div 
              onClick={() => setHelpOpen(!helpOpen)}
              className="cursor-pointer group flex items-center px-3 py-2 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              <QuestionMarkCircleIcon className="mr-3 h-5 w-5 text-gray-500 dark:text-gray-400" aria-hidden="true" />
              Help & Resources
            </div>
            
            {helpOpen && (
              <div className="mt-1 ml-8 space-y-1 text-sm">
                <Link to="/documentation" onClick={handleNavClick} className="block py-1 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white">
                  Documentation
                </Link>
                <Link to="/support" onClick={handleNavClick} className="block py-1 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white">
                  Contact Support
                </Link>
                <Link to="/terms" onClick={handleNavClick} className="block py-1 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white">
                  Terms of Service
                </Link>
                <Link to="/privacy" onClick={handleNavClick} className="block py-1 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white">
                  Privacy Policy
                </Link>
              </div>
            )}
          </>
        )}
      </div>

    </div>
  );
};

export default Sidebar;
